import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface ClientPaymentFilters {
  limit?: number;
  offset?: number;
  invoice_ref_no?: string;
  bill_date?: string;
  client_id?: string;
  gl_code?: string;
  is_advance_payment?: boolean;
  get_references?: string;
  accounting_status?: string; // Comma-separated values: e.g., "journal_posted" or "not_posted,journal_created"
  mapping_status?: string; // Comma-separated values: e.g., "unmapped,partially_mapped"
}

@Injectable({
  providedIn: 'root',
})
export class ClientPaymentService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all client payments with optional filters
   * Endpoint: GET /api/v1/client-payments/
   */
  getClientPaymentsList(filters?: ClientPaymentFilters): Observable<any> {
    let params = new HttpParams();
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key as keyof ClientPaymentFilters] !== undefined && 
            filters[key as keyof ClientPaymentFilters] !== null && 
            filters[key as keyof ClientPaymentFilters] !== '') {
          params = params.set(key, filters[key as keyof ClientPaymentFilters]!.toString());
        }
      });
    }
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-payments/`, {
      params,
      withCredentials: true,
    });
  }

  /**
   * Delete a client payment
   * Endpoint: DELETE /api/v1/client-payments/{id}
   */
  deleteClientPayment(paymentId: string): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/client-payments/${paymentId}`, {
      withCredentials: true,
    });
  }

  /**
   * Cancel a client payment
   * Endpoint: PATCH /api/v1/client-payments/{id}/cancel
   */
  cancelClientPayment(paymentId: string): Observable<any> {
    return this.http.patch<any>(`${this.API_BASE_URL}/api/v1/client-payments/${paymentId}/cancel`, {}, {
      withCredentials: true,
    });
  }

  /**
   * Create a new client payment
   * Endpoint: POST /api/v1/client-payments/
   */
  createClientPayment(payload: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/client-payments/`, payload, {
      withCredentials: true,
    });
  }

  /**
   * Update an existing client payment
   * Endpoint: PUT /api/v1/client-payments/{id}
   */
  updateClientPayment(payload: any, paymentId: string): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/client-payments/${paymentId}`, payload, {
      withCredentials: true,
    });
  }

  /**
   * Get a client payment by ID
   * Endpoint: GET /api/v1/client-payments/{id}
   */
  getClientPaymentById(paymentId: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-payments/${paymentId}`, {
      withCredentials: true,
    });
  }

  /**
   * Delete a client invoice payment mapping
   * Endpoint: DELETE /api/v1/client-invoice-payments/{id}
   */
  deleteClientInvoicePayment(invoicePaymentId: string): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/client-invoice-payments/${invoicePaymentId}`, {
      withCredentials: true,
    });
  }

  /**
   * Create a new client refund
   * Endpoint: POST /api/v1/client-refunds
   */
  createClientRefund(refundData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/client-refunds/`, refundData, {
      withCredentials: true,
    });
  }

  /**
   * Update an existing client refund
   * Endpoint: PUT /api/v1/client-refunds/{id}
   */
  updateClientRefund(id: string, refundData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/client-refunds/${id}`, refundData, {
      withCredentials: true,
    });
  }

  /**
   * Get a single client refund by ID
   * Endpoint: GET /api/v1/client-refunds/{id}
   */
  getClientRefund(id: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-refunds/${id}`, {
      withCredentials: true,
    });
  }

  /**
   * Get all client refunds with optional filters
   * Endpoint: GET /api/v1/client-refunds
   */
  getClientRefunds(filters?: any): Observable<any> {
    let params = new HttpParams();
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params = params.set(key, filters[key]);
        }
      });
    }
    
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-refunds/`, {
      params,
      withCredentials: true,
    });
  }

  /**
   * Delete a client refund
   * Endpoint: DELETE /api/v1/client-refunds/{id}
   */
  deleteClientRefund(refundId: string): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/client-refunds/${refundId}`, {
      withCredentials: true,
    });
  }

  /**
   * Cancel a client refund
   * Endpoint: PATCH /api/v1/client-refunds/{id}/cancel
   */
  cancelClientRefund(refundId: string): Observable<any> {
    return this.http.patch<any>(`${this.API_BASE_URL}/api/v1/client-refunds/${refundId}/cancel`, {}, {
      withCredentials: true,
    });
  }

  /**
   * Get client payment form templates with optional advance payment filter
   * Endpoint: GET /api/v1/client-form-templates
   */
  getClientPaymentFormTemplates(isAdvancePayment?: boolean): Observable<any> {
    let params = new HttpParams();
    
    if (isAdvancePayment !== undefined) {
      params = params.set('is_advance_form_template', isAdvancePayment.toString());
    }
    
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-payment-form-templates/`, {
      params,
      withCredentials: true,
    });
  }

  /**
   * Get a specific client payment form template with its fields
   * Endpoint: GET /api/v1/client-form-templates/{id}/with-fields
   */
  getClientPaymentFormTemplateWithFields(templateId: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-payment-form-templates/${templateId}`, {
      withCredentials: true,
    });
  }

  /**
   * Get the default client payment form template
   * Endpoint: GET /api/v1/client-form-templates/default
   */
  getDefaultClientPaymentFormTemplate(): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-form-templates/default`, {
      withCredentials: true,
    });
  }

  // ============ CLIENT REFUND FORM TEMPLATE METHODS ============

  /**
   * Get client refund form templates with optional advance payment filter
   * Endpoint: GET /api/v1/client-refund-form-templates
   */
  getClientRefundFormTemplates(): Observable<any> {
    let params = new HttpParams();
  
    
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/`, {
      params,
      withCredentials: true,
    });
  }

  /**
   * Get a specific client refund form template with its fields
   * Endpoint: GET /api/v1/client-refund-form-templates/{id}/with-custom-fields
   */
  getClientRefundFormTemplateWithFields(templateId: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/${templateId}`, {
      withCredentials: true,
    });
  }

  /**
   * Get the default client refund form template
   * Endpoint: GET /api/v1/client-refund-form-templates/default
   */
  getDefaultClientRefundFormTemplate(): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/default`, {
      withCredentials: true,
    });
  }

  /**
   * Create a new client refund form template
   * Endpoint: POST /api/v1/client-refund-form-templates
   */
  createClientRefundFormTemplate(templateData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates`, templateData, {
      withCredentials: true,
    });
  }

  /**
   * Update an existing client refund form template
   * Endpoint: PUT /api/v1/client-refund-form-templates/{id}
   */
  updateClientRefundFormTemplate(templateId: string, templateData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/${templateId}`, templateData, {
      withCredentials: true,
    });
  }

  /**
   * Get a specific client refund form template by ID
   * Endpoint: GET /api/v1/client-refund-form-templates/{id}
   */
  getClientRefundFormTemplate(templateId: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/${templateId}`, {
      withCredentials: true,
    });
  }

  /**
   * Delete a client refund form template
   * Endpoint: DELETE /api/v1/client-refund-form-templates/{id}
   */
  deleteClientRefundFormTemplate(templateId: string): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/${templateId}`, {
      withCredentials: true,
    });
  }

  /**
   * Seed the default client refund form template
   * Endpoint: POST /api/v1/client-refund-form-templates/seed-default
   */
  seedDefaultClientRefundFormTemplate(): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/client-refund-form-templates/seed-default`, {}, {
      withCredentials: true,
    });
  }

  /**
   * Get audit logs for a client refund
   * Endpoint: GET /api/v1/client-refunds/:id/audit-logs
   * Query parameters: limit, offset, date, user_id
   */
  getClientRefundAuditLogs(refundId: string | number, filterParams?: any): Observable<any> {
    let params = new HttpParams();
    
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== '') {
          params = params.set(key, filterParams[key].toString());
        }
      });
    }
    
    const url = `${this.API_BASE_URL}/api/v1/client-refunds/${refundId}/audit-logs`;
    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get audit logs for a client payment
   * Endpoint: GET /api/v1/client-payments/:id/audit-logs
   * Query parameters: limit, offset, date, user_id
   */
  getAuditLogs(paymentId: string | number, filterParams?: any): Observable<any> {
    let params = new HttpParams();
    
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== '') {
          params = params.set(key, filterParams[key].toString());
        }
      });
    }
    
    const url = `${this.API_BASE_URL}/api/v1/client-payments/${paymentId}/audit-logs`;
    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get GL Impact data for a client payment
   * Endpoint: GET /api/v1/gl-impact
   */
  getGlImpact(documentType: string, documentId: string, includeVoidEntries?: boolean): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/gl-impact`;
    let params = new HttpParams()
      .set('document_type', documentType)
      .set('document_id', documentId);
    
    if (includeVoidEntries !== undefined) {
      params = params.set('include_void_entries', includeVoidEntries.toString());
    }
    
    return this.http.get<any>(url, { params, withCredentials: true });
  }
}

